import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

interface Entry {
  id: string;
  type: string;
  subject?: string;
  inserted_at: string;
}

interface DashboardData {
  signals?: { total: number; critical: number };
  pending_approvals?: number;
  recent_entries?: Entry[];
}

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  async function loadDashboard() {
    try {
      setLoading(true);
      setError('');
      const res = await api.get('/api/dashboard');
      setData(res.data || res);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const recent = data?.recent_entries ?? [];

  return (
    <div>
      <h1 style={styles.heading}>Dashboard</h1>
      {error && <div style={styles.error}>{error}</div>}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <>
          <div style={styles.statsGrid}>
            <StatCard title="Signals" value={data?.signals?.total ?? 0} />
            <StatCard title="Critical" value={data?.signals?.critical ?? 0} />
            <StatCard title="Pending Approvals" value={data?.pending_approvals ?? 0} />
          </div>

          <h2 style={styles.subheading}>Recent Entries</h2>
          <div style={styles.card}>
            {recent.length === 0 && (
              <div style={styles.empty}>No recent activity</div>
            )}
            {recent.map((entry: Entry) => (
              <div key={entry.id} style={styles.row}>
                <div>
                  <div style={styles.rowTitle}>{entry.type}</div>
                  <div style={styles.rowMeta}>{entry.subject ?? '—'}</div>
                </div>
                <div style={styles.rowDate}>
                  {new Date(entry.inserted_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ title, value }: { title: string; value: number }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{title}</div>
      <div style={styles.statValue}>{value}</div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { fontSize: 20, fontWeight: 600, marginBottom: 16 },
  error: { background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 8, marginBottom: 12 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 },
  statCard: { background: '#fff', padding: 20, borderRadius: 8, border: '1px solid #e5e7eb' },
  statLabel: { fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
  statValue: { fontSize: 28, fontWeight: 600, marginTop: 8 },
  subheading: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  card: { background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb' },
  empty: { padding: 20, color: '#9ca3af' },
  row: { padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' },
  rowTitle: { fontWeight: 500, fontSize: 14 },
  rowMeta: { fontSize: 12, color: '#6b7280' },
  rowDate: { fontSize: 12, color: '#9ca3af' },
};
